import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, Input, Modal, Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useModulePermission } from '../../hooks/usePermission';
import { n503Api, N503ApproveItem, N503Candidate } from '../../services/n503Api';
import '../N501/N501Form.css';
import { BatchAcceptanceBridge, pwn503f1AcceptanceManifest } from '../../features/acceptance';

interface RowState {
  item: N503Candidate;
  selected: boolean;
  opinion: string;
}

const firstLinePresent = (value: string) => value.replace(/\r\n/g, '\n').split('\n')[0].trim() !== '';

const N503Form: React.FC = () => {
  const navigate = useNavigate();
  const permissions = useModulePermission('pwn503f1');
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [rows, setRows] = useState<RowState[]>([]);
  const [queried, setQueried] = useState(false);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);

  const selected = useMemo(() => rows.filter((row) => row.selected), [rows]);
  const allSelected = rows.length > 0 && rows.every((row) => row.selected);
  const someSelected = rows.some((row) => row.selected) && !allSelected;

  const query = useCallback(async () => {
    const start = startNo.trim();
    const end = endNo.trim();
    if (start && end && start > end) {
      message.warning('起始客訴單號不可大於結束客訴單號');
      return;
    }
    if (!permissions.canView) {
      message.error('沒有 N503 查詢權限');
      return;
    }
    setLoading(true);
    try {
      const response = await n503Api.query({ start_no: start, end_no: end });
      setRows((response.items || []).map((item) => ({ item, selected: false, opinion: '' })));
      setQueried(true);
      if (!response.items?.length) message.info('查無可核准的客訴單');
    } catch (error: any) {
      message.error(error.response?.data?.error || 'N503 查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [endNo, permissions.canView, startNo]);

  const patchRow = useCallback((no: string, patch: Partial<RowState>) => {
    setRows((current) => current.map((row) => (row.item.no === no ? { ...row, ...patch } : row)));
  }, []);

  const execute = useCallback(() => {
    if (!permissions.canApprove) {
      message.error('沒有 N503 核准權限');
      return;
    }
    if (selected.length === 0) {
      message.warning('請先勾選要核准的客訴單');
      return;
    }
    const invalidOpinion = selected.find((row) => !firstLinePresent(row.opinion));
    if (invalidOpinion) {
      message.warning(`客訴單 ${invalidOpinion.item.no} 的品保核准意見第一行不可空白`);
      return;
    }
    Modal.confirm({
      title: '確認品保核准',
      content: `確定核准已勾選的 ${selected.length} 筆客訴單？整批將以同一交易提交。`,
      okText: '確認核准',
      cancelText: '取消',
      onOk: async () => {
        const items: N503ApproveItem[] = selected.map((row) => ({ no: row.item.no, opinion: row.opinion }));
        setApproving(true);
        try {
          const response = await n503Api.approve(items);
          message.success(`核准完成 ${response.success} 筆`);
          await query();
        } catch (error: any) {
          message.error(error.response?.data?.error || 'N503 核准失敗，整批未寫入');
        } finally {
          setApproving(false);
        }
      },
    });
  }, [permissions.canApprove, query, selected]);

  const columns: ColumnsType<RowState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          disabled={!permissions.canApprove}
          onChange={(e) => setRows((current) => current.map((row) => ({ ...row, selected: e.target.checked })))}
        />
      ),
      key: 'selected',
      width: 48,
      align: 'center',
      render: (_, row) => (
        <Checkbox
          checked={row.selected}
          disabled={!permissions.canApprove}
          onChange={(e) => patchRow(row.item.no, { selected: e.target.checked })}
        />
      ),
    },
    { title: '客訴單號', dataIndex: ['item', 'no'], key: 'no', width: 120 },
    { title: '客戶', dataIndex: ['item', 'customer'], key: 'customer', width: 110 },
    { title: '部門', dataIndex: ['item', 'department'], key: 'department', width: 90 },
    {
      title: '賠償金額', dataIndex: ['item', 'amount'], key: 'amount', width: 110, align: 'right',
      render: (value: number) => Number(value || 0).toLocaleString(),
    },
    {
      title: '廠商賠償總額', dataIndex: ['item', 'vendor_total'], key: 'vendor_total', width: 120, align: 'right',
      render: (value: number) => Number(value || 0).toLocaleString(),
    },
    { title: '既有品保意見', dataIndex: ['item', 'opinion'], key: 'existing_opinion', width: 180, ellipsis: true },
    {
      title: '品保意見（必填）',
      key: 'new_opinion',
      width: 230,
      render: (_, row) => (
        <Input
          placeholder={`輸入 ${row.item.no} 品保意見`}
          value={row.opinion}
          disabled={!permissions.canApprove}
          status={row.selected && !firstLinePresent(row.opinion) ? 'error' : undefined}
          onChange={(e) => patchRow(row.item.no, { opinion: e.target.value })}
        />
      ),
    },
  ];

  return (
    <div className="n501-form n503-form">
      {(loading || approving) && <div className="loading-overlay">{approving ? '核准提交中…' : '查詢中…'}</div>}
      <div className="page-wrapper">
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a href="#/erp" onClick={(e) => { e.preventDefault(); navigate('/erp'); }}>ERP</a>
              <span>/</span>
              <span className="current">客訴單品保核准</span>
            </nav>
            <span className="title-separator">—</span>
            <h1>客訴單品保核准</h1>
            <span className="badge">N503</span>
          </div>
        </div>
        <div className="query-bar">
          <label data-contract-field="fn110_01">客訴單號 從</label>
          <Input
            aria-label="起始客訴單號"
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onPressEnter={query}
            allowClear
            style={{ width: 160 }}
          />
          <span className="separator">至</span>
          <Input
            aria-label="結束客訴單號"
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onPressEnter={query}
            allowClear
            style={{ width: 160 }}
          />
          <Button type="primary" disabled={!permissions.canView} loading={loading} onClick={query}>確定範圍</Button>
        </div>
        <div className="table-card">
          {queried ? (
            <Table<RowState>
              columns={columns}
              dataSource={rows}
              rowKey={(row) => row.item.no}
              pagination={false}
              size="small"
              scroll={{ x: 1150, y: 380 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">請輸入客訴單號範圍後查詢</div>
          )}
        </div>
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={!permissions.canApprove || selected.length === 0 || approving}
            loading={approving}
            onClick={execute}
          >
            品保核准 ({selected.length})
          </Button>
          <Button danger size="large" onClick={() => navigate('/erp')}>離開</Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwn503f1AcceptanceManifest}
          records={rows}
          selectedKeys={selected.map((row) => row.item.no)}
          keyOf={(row) => row.item.no}
          queryExact={(key) => n503Api.query({ start_no: key, end_no: key })}
          lastApiResponse={rows}
          loading={loading || approving}
          onAutoFill={() => rows[0] && setRows((current) => current.map((row, index) => ({ ...row, selected: index === 0 })))}
          autoFillDisabled={rows.length === 0}
          screenDraft={{ startNo, endNo }}
        />
      </div>
    </div>
  );
};

export default N503Form;
